import { ArrowDownOnSquareIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useQuoteLocation from "../../../data/useQuoteLocation";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { ILead, IQuoteLocation } from "../../../models/Lead";
import { IVehicle } from "../../../models/Vehicle";
import formatDateTime from "../../../utils/formatDateTime";
import Card from "../../shared/Card/Card";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import LoadingWheel from "../../shared/LoadingWheel";

// Address fields the quote location can push onto the vehicle.
const ADDRESS_FIELDS: { key: keyof IQuoteLocation & keyof IVehicle; label: string }[] = [
  { key: "line1", label: "Address line 1" },
  { key: "line2", label: "Address line 2" },
  { key: "line3", label: "Address line 3" },
  { key: "line4", label: "Address line 4" },
  { key: "locality", label: "Locality" },
  { key: "town", label: "Town" },
  { key: "county", label: "County" },
  { key: "postcode", label: "Postcode" },
];

const normalise = (value: string | null | undefined) => (value ?? "").trim();

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value && value.trim() !== "" ? value : "-"}</dd>
    </div>
  );
}

export default function LeadQuoteLocationTab({ lead }: { lead: ILead }) {
  const { quoteLocation } = useQuoteLocation(lead.id!);
  const data = quoteLocation.data?.data;

  const { patchVehicle } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle location details");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const vehicle = lead.vehicle;

  // Only surface the apply action when the quote address actually diverges from the vehicle.
  const differingFields = useMemo(() => {
    if (!data || !vehicle) return [];

    return ADDRESS_FIELDS.filter(
      ({ key }) => normalise(data[key] as string) !== normalise(vehicle[key] as string)
    );
  }, [data, vehicle]);

  const canApply = !!vehicle && differingFields.length > 0;

  const confirmMessage = useMemo(() => {
    if (!data || !vehicle) return "";

    const changes = differingFields
      .map(({ key, label }) => {
        const current = normalise(vehicle[key] as string) || "-";
        const next = normalise(data[key] as string) || "-";
        return `• ${label}: ${current} → ${next}`;
      })
      .join("\n");

    return `This will overwrite the vehicle's collection address with the quoted address. The following will change:\n\n${changes}`;
  }, [data, vehicle, differingFields]);

  const handleApply = () => {
    if (!vehicle || !data) return;

    // Send "" rather than null for blank quote fields so the backend (which skips null
    // values on patch) actually clears the matching vehicle field instead of leaving it.
    patchVehicle.mutate(
      {
        vehicleId: vehicle.id,
        patch: {
          line1: data.line1 ?? "",
          line2: data.line2 ?? "",
          line3: data.line3 ?? "",
          line4: data.line4 ?? "",
          locality: data.locality ?? "",
          town: data.town ?? "",
          county: data.county ?? "",
          postcode: data.postcode ?? "",
        },
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setConfirmOpen(false);
        },
        onError: saveHandlers.onError,
      }
    );
  };

  return (
    <div className="[&>*]:shadow-none [&>*]:border-0 [&>*]:rounded-none">
      <Card
        title="Quote Location"
        subtitle="The address captured when this lead was quoted. Read-only historical record."
        bodyClassName="p-4"
        collapsible
      >
        {quoteLocation.isLoading ? (
          <div className="min-h-[8rem]">
            <LoadingWheel />
          </div>
        ) : !data ? (
          <p className="text-sm text-gray-500">No quote location was captured for this lead.</p>
        ) : (
          <div className="space-y-6">
            {canApply && (
              <div className="flex items-start justify-between gap-4 rounded-md bg-amber-50 p-3">
                <p className="text-sm text-amber-800">
                  This quoted address differs from the vehicle's current collection address.
                </p>
                <ButtonInput
                  label="Apply to vehicle"
                  size="sm"
                  classes="whitespace-nowrap"
                  onClick={() => setConfirmOpen(true)}
                />
              </div>
            )}
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Full name" value={data.fullname} />
              <Field label="Email" value={data.email} />
              <Field label="Telephone 1" value={data.telephone1} />
              <Field label="Telephone 2" value={data.telephone2} />
            </dl>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Address line 1" value={data.line1} />
              <Field label="Address line 2" value={data.line2} />
              <Field label="Address line 3" value={data.line3} />
              <Field label="Address line 4" value={data.line4} />
              <Field label="Locality" value={data.locality} />
              <Field label="Town" value={data.town} />
              <Field label="County" value={data.county} />
              <Field label="Postcode" value={data.postcode} />
            </dl>
            {data.created && (
              <p className="text-xs text-gray-400">
                Captured {formatDateTime(data.created, DateTime.DATETIME_SHORT)}
              </p>
            )}
          </div>
        )}
      </Card>

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Apply quoted address to vehicle?"
        message={confirmMessage}
        confirmButtonText="Apply to vehicle"
        onConfirm={handleApply}
        isLoading={patchVehicle.isLoading}
        Icon={<ArrowDownOnSquareIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />}
        iconBg="bg-amber-100"
      />
    </div>
  );
}
